import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Progress } from '@/components/ui/progress';
import { Plus, Play, Pause, Square, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  draft: 'bg-muted text-muted-foreground',
  scheduled: 'bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/20',
  sending: 'bg-primary/10 text-primary border-primary/20',
  paused: 'bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/20',
  completed: 'bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/20',
  failed: 'bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/20',
};
const STATUS_LABELS = { draft: 'Szkic', scheduled: 'Zaplanowana', sending: 'Wysylanie', paused: 'Wstrzymana', completed: 'Zakonczona', failed: 'Blad', stopped: 'Zatrzymana' };

export default function CampaignsPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [c, t] = await Promise.all([api.get('/campaigns'), api.get('/templates')]);
      setCampaigns(c.data.items);
      setTemplates(t.data.items);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDetail = async (id) => {
    setSelectedId(id);
    setDetailLoading(true);
    try {
      const r = await api.get(`/campaigns/${id}/detail`);
      setDetail(r.data);
    } catch { toast.error('Blad ladowania szczegolow'); }
    finally { setDetailLoading(false); }
  };

  const handleStart = async (id) => {
    try { await api.post(`/campaigns/${id}/start`); toast.success('Kampania uruchomiona'); fetchData(); if (selectedId === id) openDetail(id); }
    catch (err) { toast.error(err.response?.data?.detail || 'Blad'); }
  };

  const handlePause = async (id) => {
    try { await api.post(`/campaigns/${id}/pause`); toast.success('Kampania zapauzowana'); fetchData(); if (selectedId === id) openDetail(id); }
    catch (err) { toast.error(err.response?.data?.detail || 'Blad'); }
  };

  const handleResume = async (id) => {
    try { await api.post(`/campaigns/${id}/resume`); toast.success('Kampania wznowiona'); fetchData(); if (selectedId === id) openDetail(id); }
    catch (err) { toast.error(err.response?.data?.detail || 'Blad'); }
  };

  const handleStop = async (id) => {
    if (!window.confirm('Zatrzymac kampanie na stale? Nienwyslane SMS zostaną anulowane.')) return;
    try { await api.post(`/campaigns/${id}/stop`); toast.success('Kampania zatrzymana'); fetchData(); if (selectedId === id) openDetail(id); }
    catch (err) { toast.error(err.response?.data?.detail || 'Blad'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Usunac kampanie?')) return;
    try { await api.delete(`/campaigns/${id}`); toast.success('Usunieto'); setSelectedId(null); fetchData(); }
    catch { toast.error('Blad'); }
  };

  const getTemplateName = (id) => templates.find(t => t.id === id)?.name || '-';
  const progressPct = (c) => { const total = c.stats?.total || 0; const done = (c.stats?.sent || 0) + (c.stats?.failed || 0); return total > 0 ? Math.round(done / total * 100) : 0; };

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Kampanie</h1>
          <p className="text-sm text-muted-foreground mt-1">Tworzenie i zarzadzanie kampaniami</p>
        </div>
        <Button onClick={() => navigate('/campaigns/new')} data-testid="campaigns-add-button"><Plus className="h-4 w-4 mr-2" />Kreator kampanii</Button>
      </div>

      <Card className="border-border/50 bg-card/80">
        <CardContent className="p-0">
          {loading ? <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}</div> : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Nazwa</TableHead>
                  <TableHead>Typ</TableHead>
                  <TableHead>Grupa/Baza</TableHead>
                  <TableHead>Szablon</TableHead>
                  <TableHead>Postep</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-32">Akcje</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Brak kampanii. Uzyj kreatora!</TableCell></TableRow>
                ) : campaigns.map(c => (
                  <TableRow key={c.id} className="hover:bg-muted/30 transition-colors duration-150 cursor-pointer" onClick={() => openDetail(c.id)}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{c.type?.toUpperCase()}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{c.send_config?.target_group === 'all' ? 'Wszystkie' : c.send_config?.target_group || '-'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{getTemplateName(c.template_id)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={progressPct(c)} className="h-1.5 w-20" />
                        <span className="text-xs font-mono text-muted-foreground">{c.stats?.sent || 0}/{c.stats?.total || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell><Badge variant="outline" className={STATUS_STYLES[c.status] || ''}>{STATUS_LABELS[c.status] || c.status}</Badge></TableCell>
                    <TableCell>
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDetail(c.id)} data-testid={`campaign-detail-${c.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                        {['draft', 'scheduled', 'paused'].includes(c.status) && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--success))]" onClick={() => handleStart(c.id)} data-testid={`campaign-start-${c.id}`}><Play className="h-3.5 w-3.5" /></Button>
                        )}
                        {c.status === 'sending' && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--warning))]" onClick={() => handlePause(c.id)} data-testid={`campaign-pause-${c.id}`}><Pause className="h-3.5 w-3.5" /></Button>
                        )}
                        {c.status === 'paused' && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--success))]" onClick={() => handleResume(c.id)} data-testid={`campaign-resume-${c.id}`}><Play className="h-3.5 w-3.5" /></Button>
                        )}
                        {['sending', 'paused'].includes(c.status) && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleStop(c.id)} data-testid={`campaign-stop-${c.id}`}><Square className="h-3.5 w-3.5" /></Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(c.id)} data-testid={`campaign-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Sheet open={!!selectedId} onOpenChange={(open) => { if (!open) setSelectedId(null); }}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading">Szczegoly kampanii</SheetTitle>
            <SheetDescription>Statystyki i zadania wysylki</SheetDescription>
          </SheetHeader>
          {detailLoading ? (
            <div className="space-y-4 mt-4"><Skeleton className="h-20" /><Skeleton className="h-20" /><Skeleton className="h-40" /></div>
          ) : detail ? (
            <div className="space-y-4 mt-4">
              <div>
                <h3 className="font-medium text-base">{detail.name}</h3>
                <Badge variant="outline" className={`mt-1 ${STATUS_STYLES[detail.status] || ''}`}>{STATUS_LABELS[detail.status] || detail.status}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                  <p className="text-xs text-muted-foreground">Wyslane</p>
                  <p className="text-lg font-semibold font-heading tabular-nums text-[hsl(var(--success))]">{detail.task_stats?.sent || 0}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                  <p className="text-xs text-muted-foreground">Bledy</p>
                  <p className="text-lg font-semibold font-heading tabular-nums text-[hsl(var(--destructive))]">{detail.task_stats?.failed || 0}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                  <p className="text-xs text-muted-foreground">W kolejce</p>
                  <p className="text-lg font-semibold font-heading tabular-nums">{detail.task_stats?.queued || 0}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                  <p className="text-xs text-muted-foreground">Przypisane</p>
                  <p className="text-lg font-semibold font-heading tabular-nums">{detail.task_stats?.assigned || 0}</p>
                </div>
              </div>
              {detail.task_stats?.total > 0 && (
                <div>
                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>Postep</span>
                    <span>{Math.round(((detail.task_stats.sent + detail.task_stats.failed) / detail.task_stats.total) * 100)}%</span>
                  </div>
                  <Progress value={((detail.task_stats.sent + detail.task_stats.failed) / detail.task_stats.total) * 100} className="h-2" />
                </div>
              )}
              {(detail.actual_body || detail.template) && (
                <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                  {detail.actual_subject && <p className="text-xs font-medium mb-1">Temat: {detail.actual_subject}</p>}
                  <p className="text-xs text-muted-foreground mb-1">Szablon: {detail.template?.name || '-'}</p>
                  <div className="text-xs whitespace-pre-wrap mt-2 prose prose-invert prose-xs max-w-none" dangerouslySetInnerHTML={{ __html: detail.actual_body || detail.template?.body || '' }} />
                </div>
              )}
              {detail.recent_messages?.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Ostatnie wiadomosci</p>
                  <div className="space-y-1 max-h-60 overflow-y-auto">
                    {detail.recent_messages.slice(0, 10).map(m => (
                      <div key={m.id} className="flex items-center gap-2 text-xs py-1.5 px-2 rounded hover:bg-muted/30">
                        <Badge variant="outline" className={`text-[10px] w-14 justify-center ${m.status === 'sent' ? 'text-[hsl(var(--success))]' : m.status === 'failed' ? 'text-destructive' : 'text-muted-foreground'}`}>{m.status}</Badge>
                        <span className="font-mono text-muted-foreground">{m.to_email || m.phone || m.email || '-'}</span>
                        {m.retry_count > 0 && <span className="text-muted-foreground">retry:{m.retry_count}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}
